import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output, Renderer2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { animate, AnimationMetadata, style } from '@angular/animations'
import { Subject, Subscription } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { OverlayHelper } from '../overlay/overlay-helper'
import { AnimationService } from '../animation.service'

let zIndex = 9999

export interface PopupAnimations {
  enter?: AnimationMetadata | AnimationMetadata[]
  leave?: AnimationMetadata | AnimationMetadata[]
}

export enum Position {
  center = 'center',
  top = 'top',
  right = 'right',
  bottom = 'bottom',
  left = 'left'
}
@Component({
  selector: 'ngx-popup',
  templateUrl: './popup.component.html',
  styleUrls: ['./popup.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PopupComponent),
      multi: true
    }
  ]
})
export class PopupComponent implements ControlValueAccessor, OnDestroy {
  /* inputs
  -------------------------- */
  @Input() position: Position = Position.center    /** å¼¹çªä½ç½® */
  @Input() animations: PopupAnimations = {}        /** èªå®ä¹å¨ç» */
  @Input() overlay: boolean = true                 /** æ¯å¦æ¾ç¤ºèç */
  @Input() overlayOpacity: number = 0.5            /** èçéæåº¦ 0~1 */
  @Input() closeOnClickOverlay: boolean = true     /** æ¯å¦åè®¸ç¹å»èçæ¶èªå¨å³é­å¼¹æ¡ */
  @Input() zIndex: number = zIndex++               /** å css z-index */
  @Input() externalClass: object = {}              /** èªå®ä¹ç±»å */
  /* outputs
  -------------------------- */
  @Output() clickOverlay = new EventEmitter<any>() /** ç¹å»èçæ¶è§¦å */
  @Output() beforeOpen = new EventEmitter<any>()   /** æå¼ä¹åè§¦åï¼è¿æªæ§è¡è¿åºå¨ç»ï¼ */
  @Output() afterOpen = new EventEmitter<any>()    /** æå¼ä¹åè§¦åï¼è¿åºå¨ç»æ§è¡å®æ¯ï¼ */
  @Output() beforeClose = new EventEmitter<any>()  /** å³é­ä¹åè§¦åï¼è¿æªæ§è¡ç¦»åºå¨ç»ï¼ */
  @Output() afterClose = new EventEmitter<any>()   /** å³é­ä¹åè§¦åï¼ç¦»åºå¨ç»æ§è¡å®æ¯ï¼ */

  @ViewChild('container', { static: false }) private container: ElementRef

  visible: boolean
  get el() {
    return this.container.nativeElement
  }

  private destroy$ = new Subject<any>()
  private animationSub: Subscription
  private dirty = false
  private leaving = false
  private onChange: (value: any) => void
  private closeOverlay: () => void

  constructor(
    private hostElRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private overlayHelper: OverlayHelper,
    private animation: AnimationService
  ) {}

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  registerOnChange(fn: any) {
    this.onChange = fn
  }

  registerOnTouched(fn: any) {}

  writeValue(value: boolean) {
    if (value === null) { return }
    if (!this.dirty && !value) { return }
    if (this.animationSub) { this.animationSub.unsubscribe() }

    if (value) {
      this.open()
    } else {
      this.close()
    }

    this.onChange(value)
    this.dirty = true
  }

  private open() {
    // é¿åéªç
    this.renderer.setStyle(this.el, 'opacity', 0)
    this.visible = true
    this.cdr.markForCheck()
    this.beforeOpen.emit()
    this.leaving = false
    // ä¼åæ§è½
    setTimeout(() => {
      this.renderer.removeStyle(this.el, 'opacity')
      this.overlay && this.openOverlay()
      this.animationSub = this.makeAnimation('enter')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.afterOpen.emit()
        })
    }, 0)
  }

  private close() {
    this.beforeClose.emit()
    this.leaving = true
    this.overlay && this.closeOverlay()
    this.animationSub = this.makeAnimation('leave')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.visible = false
        this.cdr.detectChanges()
        this.afterClose.emit()
        this.leaving = false
      })
  }

  private openOverlay() {
    this.closeOverlay = this.overlayHelper.open({
      opacity: this.overlayOpacity,
      zIndex: this.zIndex,
      getContainer: () => this.hostElRef.nativeElement,
      onClick: () => {
        this.clickOverlay.emit()
        if (this.closeOnClickOverlay && this.visible && !this.leaving) {
          this.writeValue(false)
        }
      }
    })
  }

  private makeAnimation(state: 'enter' | 'leave') {
    const el = this.container.nativeElement
    const animation = state === 'leave' ? this.getLeaveAnimation() : this.getEnterAnimation()

    return this.animation.makeAnimation(el, animation)
  }

  private getEnterAnimation() {
    return this.animations.enter || {
      [Position.center]: [ style({ opacity: 0 }), animate('.3s ease', style({ opacity: 1 })) ],
      [Position.top]   : [ style({ transform: 'translate3d(0, -100%, 0)' }), animate('.3s ease', style({ transform: 'translate3d(0, 0, 0)' })) ],
      [Position.right] : [ style({ transform: 'translate3d(100%, 0, 0)' }), animate('.3s ease', style({ transform: 'translate3d(0, 0, 0)' })) ],
      [Position.bottom]: [ style({ transform: 'translate3d(0, 100%, 0)' }), animate('.3s ease', style({ transform: 'translate3d(0, 0, 0)' })) ],
      [Position.left]  : [ style({ transform: 'translate3d(-100%, 0, 0)' }), animate('.3s ease', style({ transform: 'translate3d(0, 0, 0)' })) ]
    }[this.position]
  }

  private getLeaveAnimation() {
    return this.animations.leave || {
      [Position.center]: [ style({ opacity: 1 }), animate('.3s ease', style({ opacity: 0 })) ],
      [Position.top]   : [ style({ transform: 'translate3d(0, 0, 0)' }), animate('.3s ease', style({ transform: 'translate3d(0, -100%, 0)' })) ],
      [Position.right] : [ style({ transform: 'translate3d(0, 0, 0)' }), animate('.3s ease', style({ transform: 'translate3d(100%, 0, 0)' })) ],
      [Position.bottom]: [ style({ transform: 'translate3d(0, 0, 0)' }), animate('.3s ease', style({ transform: 'translate3d(0, 100%, 0)' })) ],
      [Position.left]  : [ style({ transform: 'translate3d(0, 0, 0)' }), animate('.3s ease', style({ transform: 'translate3d(-100%, 0, 0)' })) ]
    }[this.position]
  }
}
